/**
 * The writer's edits in canonical form (decision 66; tables K, V, G and P in the part 10 log). An edit removes units
 * the writer sees and puts new ones in: text (typed, pasted as plain text), markup (a line end, a pasted source's
 * markup) or a conversion (table S). The window of top-level blocks around the edit is written again with the new units,
 * text escaped so it stays text wherever it lands (table K); the written window is parsed and checked against what the
 * writer saw (V1), and when the check fails the constructs around the edit are written as text (V2–V4). The result is
 * the window's canonical form, spliced back without touching the rest of the chapter (table G).
 *
 * Principles 1–4 in the part 10 log: the chapter stays canonical; the result shows what the writer saw with the edit
 * made; text stays text; the cost follows the edit.
 */
import { chapterModel, isAllowedLink, parseChapter, serializeChapter } from '@cockatrice/core';
import type { Block, ChapterModel, Inline, ParsedChapter } from '@cockatrice/core';
import type { SyntaxNode } from '@lezer/common';
import { unitsOf } from './units.ts';
import type { Unit, UnitKind } from './units.ts';

/** Text (escaped where it lands), markup inside or outside an inline element, or markup a conversion makes (table S). */
export type PieceKind = 'text' | 'inline' | 'block' | 'convert';

/** Inserted characters, all of one kind. */
export interface Piece {
  readonly kind: PieceKind;
  readonly text: string;
  /** Markup whose line ends carry their container prefix already; otherwise placing adds it (P1). */
  readonly prefixed?: boolean;
  /**
   * Markup a command puts in (a break, a line end, a container prefix): never written as text by a later stage, since it
   * is what the writer asked for (V2–V4).
   */
  readonly keep?: boolean;
  /** A block break the spans around it were closed and opened again for already (P11). */
  readonly split?: boolean;
}

/** One edit of the source: `from`–`to` removed (widened to whole units, P7) and `insert` put in its place. */
export interface Edit {
  readonly from: number;
  readonly to: number;
  readonly insert: readonly Piece[];
  /**
   * Typed, pasted or dropped: an insertion point on an empty line or in a value zone or mark is placed (P2–P5), and each
   * line end of inserted markup continues the containers at the point (P1).
   */
  readonly place?: boolean;
  /** Markup put in after `insert`: the point after the edit is before it (a block pending above, N1). */
  readonly after?: readonly Piece[];
  /** Typed (by key, the DOM or an input method): only typing edits a value or a mark as markup (P2, P3, *deep-fix r4*). */
  readonly typed?: boolean;
}

/** One change of the old source, in its positions. */
export interface SourceChange {
  readonly from: number;
  readonly to: number;
  readonly insert: string;
}

/** Where one edit ended up in the new source: right before and right after what it put in (where it removed). */
export interface Point<E extends Edit> {
  /** The edit, as given. */
  readonly edit: E;
  readonly start: number;
  readonly end: number;
}

/**
 * Table V's stages: which markup is written as text (1: none; 2: the inline constructs the edits meet; 3: those and the
 * touched lines' block markup; 4: all markup of the touched lines, and every text unit by table K).
 */
export type Stage = 1 | 2 | 3 | 4;

/** The edits made: the changes to the old source and where each edit ended up. */
export interface Rewritten<E extends Edit> {
  /** Sorted and apart (table X, X1). */
  readonly changes: readonly SourceChange[];
  /** One per edit, in the order given. */
  readonly points: readonly Point<E>[];
  /** The highest stage of table V a window needed. */
  readonly stage: Stage;
  /** The windows rewritten, in the old source's positions (tables G; principle 4: what an edit costs). */
  readonly windows: readonly { readonly from: number; readonly to: number }[];
}

/** A source read once: its parse, its model and its units (table U), in its own positions. */
export interface Reading {
  readonly parsed: ParsedChapter;
  readonly model: ChapterModel;
  readonly units: readonly Unit[];
}

/** ASCII punctuation: CommonMark lets a backslash escape every one of them, and reads each escaped one as text. */
const PUNCTUATION = /[!-/:-@[-`{-~]/u;

const isLineEnd = (show: string): boolean => show === '\n';
const isBlank = (show: string): boolean => show === ' ' || show === '\t';

/** One unit of the edited window, old or put in, and how it is to be read. */
interface Entry {
  /** What the writer sees (table U). */
  readonly show: string;
  /** The source to write when it is written as it was: the old source, or inserted markup. */
  readonly raw: string;
  /** Text, markup (inline, block, value, label) as it was, or markup a conversion makes. */
  readonly kind: UnitKind | 'convert';
  /**
   * How old text is written: a literal character, a reference (an escape or entity, text in every context), or a
   * delimiter run's unmatched part (written with its run, L6); null for markup and inserted units.
   */
  readonly origin: Unit['origin'];
  /** The old unit's start in the window, or -1 for an inserted unit. */
  readonly at: number;
  /** Inserted by an edit (always written by table K when it is text). */
  readonly inserted: boolean;
  /** Markup a command put in, never written as text (Piece.keep). */
  readonly keep: boolean;
}

/** An old inline element's extent, in window positions, for V1 and V2; a line break depends on its line, not only itself. */
interface Extent {
  readonly from: number;
  readonly to: number;
  readonly lineBreak: boolean;
  /** An emphasis, strong, small caps or link: a span an edit can leave empty (R10). */
  readonly span: boolean;
}

function elementExtents(items: readonly Inline[], out: Extent[]): void {
  for (const item of items) {
    if (item.kind === 'text') continue;
    out.push({
      from: item.from,
      to: item.to,
      lineBreak: item.kind === 'hard-break' || item.kind === 'soft-break',
      span: item.kind === 'emphasis' || item.kind === 'strong' || item.kind === 'small-caps' || item.kind === 'link',
    });
    if (item.kind === 'emphasis' || item.kind === 'strong' || item.kind === 'small-caps' || item.kind === 'link' || item.kind === 'image' || item.kind === 'brackets') {
      elementExtents(item.children, out);
    }
  }
}

/** Every leaf's inline elements, in window positions. */
function extentsOf(blocks: readonly Block[], out: Extent[] = []): Extent[] {
  for (const block of blocks) {
    if (block.kind === 'paragraph' || block.kind === 'heading' || block.kind === 'centered') elementExtents(block.content, out);
    else if (block.kind === 'quote' || block.kind === 'footnote') extentsOf(block.blocks, out);
    else if (block.kind === 'list') for (const item of block.items) extentsOf(item, out);
  }
  return out;
}

/**
 * The container prefix a new line at `pos` continues with, outermost first: `> ` per quote, an item's indent (its
 * mark's width and one blank), a note's four spaces.
 */
export function continuation(parsed: ParsedChapter, pos: number): string {
  const parts: string[] = [];
  for (let node: SyntaxNode | null = parsed.tree.resolveInner(pos, -1); node !== null; node = node.parent) {
    if (node.name === 'Blockquote') parts.push('> ');
    else if (node.name === 'FootnoteDefinition') parts.push('    ');
    else if (node.name === 'ListItem') {
      // An item's first child is its mark.
      const mark = node.cursor();
      mark.firstChild();
      parts.push(' '.repeat(mark.to - node.from + 1));
    }
  }
  return parts.reverse().join('');
}

/** A window of top-level blocks: its outer neighbours, which the rewrite must leave as they were (G1, G2). */
interface Window {
  readonly from: number;
  readonly to: number;
  readonly left: SyntaxNode | null;
  readonly right: SyntaxNode | null;
}

/**
 * The window between two neighbours, or reaching the chapter's edge where there is none. It ends with the right
 * neighbour's line end, so every window reads as a chapter ending in one, as the stored form does.
 */
function between(left: SyntaxNode | null, right: SyntaxNode | null, length: number): Window {
  return { from: left?.from ?? 0, to: right === null ? length : Math.min(length, right.to + 1), left, right };
}

/** The window around `from`–`to`: the top-level blocks it meets, ends included, and one more on each side (G1). */
function windowAround(parsed: ParsedChapter, from: number, to: number): Window {
  const top = parsed.tree.topNode;
  // The last block starting at or before `from` and the first ending at or after `to`; on a blank line, the blocks
  // on both sides of it.
  const first = top.childBefore(from + 1);
  const last = top.childAfter(to - 1);
  return between(first?.prevSibling ?? null, last?.nextSibling ?? null, parsed.text.length);
}

/**
 * A window inside a quote (G7, *deep-fix r4*): whole lines of the quote's children around the edit, the
 * lines keeping their prefixes, so the slice reads alone as it does in the chapter; what lies before and after the
 * edited child (its neighbours and the separators between) must come back exactly as it was (G8).
 */
interface NestedWindow {
  readonly from: number;
  readonly to: number;
  readonly before: string;
  readonly after: string;
}

/**
 * Containers whose children a window may slice: a quote's blocks (G7). Not a list's items: whether a list is loose is the
 * whole list's, so an edit in one item can change the separators between any two (G10, found by the G7 property); not a
 * note's or an item's blocks, whose lines read differently alone (indented).
 */
const SLICED = /^Blockquote$/u;

/** A container's own markup among its children: quote marks. */
const CONTAINER_MARKS: ReadonlySet<string> = new Set(['QuoteMark']);

/** The innermost window inside a quote `container` holding `from`–`to` in one child, or null (G7, G9). */
function nestedWindow(source: string, container: SyntaxNode, from: number, to: number): NestedWindow | null {
  const kids: SyntaxNode[] = [];
  for (let child = container.firstChild; child !== null; child = child.nextSibling) if (!CONTAINER_MARKS.has(child.name)) kids.push(child);
  const index = kids.findIndex((kid) => kid.from <= from && to <= kid.to);
  const kid = kids[index];
  if (kid === undefined) return null;
  if (SLICED.test(kid.name)) {
    const deeper = nestedWindow(source, kid, from, to);
    if (deeper !== null) return deeper;
  }
  const lineStart = (pos: number): number => source.lastIndexOf('\n', pos - 1) + 1;
  const lineEnd = (pos: number): number => {
    const end = source.indexOf('\n', pos);
    return end < 0 ? source.length : end;
  };
  // A child at the container's edge could change what the container reads as to the blocks around it (a list made a
  // paragraph, a bullet alternating with the list after it): only a child with neighbours on both sides is sliced.
  const left = kids[index - 1];
  const right = kids[index + 1];
  if (left === undefined || right === undefined) return null;
  const start = lineStart(left.from);
  const end = Math.min(source.length, lineEnd(right.to) + 1);
  return { from: start, to: end, before: source.slice(start, lineStart(kid.from)), after: source.slice(lineEnd(kid.to), end) };
}

/** The edits of one window, in window positions, each with the edit it came from. */
interface LocalEdit<E extends Edit> {
  readonly from: number;
  readonly to: number;
  readonly insert: readonly Piece[];
  /** Markup placed after the writer's units (P4): the point after the edit is before it. */
  readonly after: readonly Piece[];
  readonly origin: E;
}

/** The units of the line holding `pos` (in window positions) and the line's extent. */
function lineOf(text: string, units: readonly Unit[], pos: number): { start: number; end: number; units: Unit[] } {
  const start = text.lastIndexOf('\n', pos - 1) + 1;
  const found = text.indexOf('\n', pos);
  const end = found < 0 ? text.length : found;
  return { start, end, units: units.filter((u) => u.from >= start && u.from < end) };
}

/** Each line end of `pieces`' unprefixed markup followed by `prefix` (P1). */
export function prefixLines(pieces: readonly Piece[], prefix: string): Piece[] {
  return pieces.map((p) => (p.kind === 'text' || p.prefixed === true ? p : { kind: p.kind, text: p.text.replaceAll('\n', `\n${prefix}`), prefixed: true }));
}

/** Inline spans a block break inside closes and opens again (*deep-fix r13*). */
const SPANS: ReadonlySet<string> = new Set(['Emphasis', 'StrongEmphasis', 'SmallCaps', 'Link']);

/**
 * Whether a node is a span: emphasis, strong, small caps, or a link with a destination. Brackets the parse marks as a
 * link with none (`[sic]`, `[a][b]`) are text in the canonical form, which holds no definitions (G4; *deep-fix r19*).
 */
export function isSpan(node: SyntaxNode): boolean {
  return SPANS.has(node.name) && (node.name !== 'Link' || node.getChild('URL') !== null);
}

/** The marks spans are made of. */
const SPAN_MARKS: ReadonlySet<string> = new Set(['EmphasisMark', 'SmallCapsMark', 'LinkMark']);

/** A span's opening mark, and its closing marks (a link's from its `]` to its end). */
export function spanMarks(node: SyntaxNode): { open: { from: number; to: number }; close: { from: number; to: number } } {
  const marks: SyntaxNode[] = [];
  // Its own marks only: a quote's `>` on a line the span crosses is the quote's (*deep-fix r16*).
  for (let child = node.firstChild; child !== null; child = child.nextSibling) if (SPAN_MARKS.has(child.name)) marks.push(child);
  const first = marks.reduce<SyntaxNode>((found, mark, i) => (i === 0 ? mark : found), node);
  // A link closes from its second mark, `]`; any other span with its last.
  const last = marks.reduce<SyntaxNode>((found, mark, i) => (node.name !== 'Link' || i === 1 ? mark : found), node);
  return { open: { from: first.from, to: first.to }, close: { from: last.from, to: node.name === 'Link' ? node.to : last.to } };
}

/**
 * N1, P11 (*deep-fix r14, r15*, the user's choice to read it from the parse): what a block break put in at `from`–`to`
 * inside spans takes with it. Read from the units of the leaf's own lines, so a quote's `>` or an item's indent is
 * never taken for the writer's blanks: going back, with `takeLineEnd`, the container marks of the line it starts and the
 * line end before them (soft, or a backslash break's `\` and line end), which the break replaces; then, either way, the
 * text blanks before it; going on, the text blanks after it. The blanks come back as text, outside the spans' marks.
 */
export function splitBlanks(parsed: ParsedChapter, from: number, to: number, takeLineEnd: boolean): { from: number; to: number; before: string; after: string } {
  const { text, tree } = parsed;
  /** Whether only container marks stand between `at`'s line start and it. */
  const marksOnly = (at: number): boolean => {
    for (let k = text.lastIndexOf('\n', at - 1) + 1; k < at; k += 1) {
      const char = text.charAt(k);
      if (char !== ' ' && char !== '\t' && !(char === '>' && tree.resolveInner(k, 1).name === 'QuoteMark')) return false;
    }
    return true;
  };
  let first = from;
  // A continuation line's start: only container marks before the point (a quote's `>` the parse marks, blanks of the
  // prefix or an item's indent: the canonical form writes a line's leading text blanks as references), then the line
  // end, soft or a break the parse marks (a backslash's or two spaces').
  const lineStart = text.lastIndexOf('\n', from - 1) + 1;
  // Called only inside a span (N1, P11): with only container marks before the point on its line, the span opened on an
  // earlier line, so the line end before this one is inside its leaf, a soft or hard break, never one between blocks.
  if (takeLineEnd && lineStart > 0 && marksOnly(from)) {
    const lineBreak = tree.resolveInner(lineStart - 1, -1);
    first = lineBreak.name === 'HardBreak' && lineBreak.to === lineStart ? lineBreak.from : lineStart - 1;
  }
  // Text blanks beside it: raw blanks, or a reference to one.
  const blankBack = (at: number): number => {
    // Blanks among a line's container marks are the marks'.
    if (marksOnly(at)) return 0;
    const char = text.charAt(at - 1);
    if (char === ' ' || char === '\t') return 1;
    const node = tree.resolveInner(at, -1);
    return node.name === 'Entity' && BLANK_REFERENCE.test(text.slice(node.from, at)) ? at - node.from : 0;
  };
  const blankOn = (at: number): number => {
    const char = text.charAt(at);
    if (char === ' ' || char === '\t') return 1;
    const node = tree.resolveInner(at, 1);
    return node.name === 'Entity' && BLANK_REFERENCE.test(text.slice(at, node.to)) ? node.to - at : 0;
  };
  let before = '';
  for (let n = blankBack(first); n > 0; n = blankBack(first)) {
    before = shownBlank(text.slice(first - n, first)) + before;
    first -= n;
  }
  let last = to;
  let after = '';
  for (let n = blankOn(last); n > 0; n = blankOn(last)) {
    after += shownBlank(text.slice(last, last + n));
    last += n;
  }
  return { from: first, to: last, before, after };
}

/** A character reference to a blank: a space or a tab. */
const BLANK_REFERENCE = /^&#(?:32|x20|9|x9);$/iu;

/** The blank a raw blank or a reference to one shows. */
function shownBlank(raw: string): string {
  return /^&#(?:9|x9);$/iu.test(raw) || raw === '\t' ? '\t' : ' ';
}

/**
 * N1, P11 (*deep-fix r14*): a point at the edge of spans' content, moved outside their marks: before the opening marks
 * at the content's start, after the closing marks at its end. A block break there needs no span closed or opened.
 */
export function outsideSpans(parsed: ParsedChapter, pos: number): number {
  let point = pos;
  for (let moved = true; moved; ) {
    moved = false;
    for (const side of [-1, 1] as const) {
      for (let node: SyntaxNode | null = parsed.tree.resolveInner(point, side); node !== null; node = node.parent) {
        if (!isSpan(node)) continue;
        const marks = spanMarks(node);
        const next = marks.open.to === point ? marks.open.from : marks.close.from === point ? marks.close.to : point;
        if (next !== point) {
          point = next;
          moved = true;
        }
      }
    }
  }
  return point;
}

/**
 * N1, P10 (*deep-fix r13*): the spans `from`–`to` lies inside (between their marks): the marks closing them, innermost
 * first, and opening them again, outermost first, for a block break put in there, so the formatting goes on in the next
 * block as a centered block's does.
 */
export function openSpans(parsed: ParsedChapter, from: number, to: number = from): { close: Piece[]; open: Piece[] } {
  const close: Piece[] = [];
  const open: Piece[] = [];
  for (let node: SyntaxNode | null = parsed.tree.resolveInner(from, 1); node !== null; node = node.parent) {
    if (!isSpan(node)) continue;
    const marks = spanMarks(node);
    // A point at its content's edge is outside it (`outsideSpans` moves it there first); a range reaching an edge, inside
    // (*deep-fix r24*: a double-clicked first or last word pasted over).
    if (from === to ? marks.open.to >= from || to >= marks.close.from : marks.open.to > from || to > marks.close.from) continue;
    close.push({ kind: 'inline', text: parsed.text.slice(marks.close.from, marks.close.to), prefixed: true, keep: true });
    open.unshift({ kind: 'inline', text: parsed.text.slice(marks.open.from, marks.open.to), prefixed: true, keep: true });
  }
  return { close, open };
}

/**
 * P11 (*deep-fix r13, r14*): `pieces` with each block break inside spans closing them before it and the blanks before
 * it, and opening them again after it and the blanks after it: blanks stay text, outside the marks (as H3 keeps them).
 */
function splitSpans(pieces: readonly Piece[], spans: { close: readonly Piece[]; open: readonly Piece[] }): Piece[] {
  // Text pieces cut into their blank ends and the rest, so blanks next to a break can move past the marks.
  const cut = pieces.flatMap((p): Piece[] => {
    if (p.kind !== 'text') return [p];
    const core = p.text.trim();
    const start = p.text.indexOf(core);
    const parts = core === '' ? [p.text] : [p.text.slice(0, start), core, p.text.slice(start + core.length)];
    return parts.filter((t) => t !== '').map((t) => ({ kind: 'text', text: t }));
  });
  const blank = (p: Piece | undefined): boolean => p?.kind === 'text' && p.text.trim() === '';
  const out: Piece[] = [];
  let reopen = false;
  for (const p of cut) {
    if (isSeparator(p) && p.split !== true) {
      const trailing: Piece[] = [];
      while (blank(out.at(-1))) trailing.unshift(...out.splice(-1));
      out.push(...spans.close, ...trailing, { ...p, split: true });
      reopen = true;
      continue;
    }
    if (reopen && !blank(p)) {
      out.push(...spans.open);
      reopen = false;
    }
    out.push(p);
  }
  if (reopen) out.push(...spans.open);
  return out;
}

/** Whether a piece ends a block: markup holding a line end (a paste's paragraph break). */
const isSeparator = (piece: Piece): boolean => piece.kind === 'block' && piece.keep !== true && piece.text.includes('\n');

/** The content of the centered block holding `from`–`to`, between its marks; or null. */
function centeredContent(parsed: ParsedChapter, from: number, to: number): { from: number; to: number } | null {
  for (let node: SyntaxNode | null = parsed.tree.resolveInner(from, 1); node !== null; node = node.parent) {
    if (node.name !== 'CenteredBlock') continue;
    const open = node.firstChild;
    const close = node.lastChild;
    if (open === null || close === null || from < open.to || to > close.from) return null;
    return { from: open.to, to: close.from };
  }
  return null;
}

/** The marks a block break piece opens the next block with (a heading's, an item's, a quote's), if any. */
const marksAfter = (piece: Piece): string => piece.text.slice(piece.text.lastIndexOf('\n') + 1);

/** A centered block's mark, put in by P10. */
const centerMark = (text: '<-' | '->'): Piece => ({ kind: 'block', text, prefixed: true, keep: true });

/**
 * P10 (*deep-fix r8, r9*): pieces put in a centered block. Its first block's text joins the block; a break closes it;
 * a paragraph after a break opens a centered block again, as Enter's split does (N1); a heading, list, quote or scene
 * break stands on its own. When the pieces end outside a centered block, the rest of the block opens one again, or,
 * at its end, the pieces take its closing mark (`swallow`); a final break where the block ends anyway goes.
 */
function splitCentered(insert: readonly Piece[], atEnd: boolean): { insert: Piece[]; swallow: boolean } {
  const last = insert.at(-1);
  const pieces = atEnd && last !== undefined && isSeparator(last) && marksAfter(last) === '' ? insert.slice(0, -1) : insert;
  const out: Piece[] = [];
  let open = true;
  for (const p of pieces) {
    if (!isSeparator(p)) {
      out.push(p);
      continue;
    }
    if (open) out.push(centerMark('<-'));
    out.push({ ...p, keep: true });
    open = marksAfter(p).trim() === '';
    if (open) out.push(centerMark('->'));
  }
  if (open || atEnd) return { insert: out, swallow: !open };
  return { insert: [...out, { kind: 'block', text: '\n\n', keep: true }, centerMark('->')], swallow: false };
}

/**
 * P2: the value zone `from`–`to` is in or beside: a destination's or title's (`value`), or a note label's (`label`), when
 * a point touches one or a range holds only its characters; else null.
 */
function valueZone(units: readonly Unit[], from: number, to: number): 'value' | 'label' | null {
  const kinds = new Set(
    from === to
      ? [units.find((u) => u.to === from)?.kind, units.find((u) => u.from === from)?.kind]
      : units.filter((u) => u.from >= from && u.to <= to).map((u) => u.kind),
  );
  if (kinds.has('label') && (from === to || kinds.size === 1)) return 'label';
  if (kinds.has('value') && (from === to || kinds.size === 1)) return 'value';
  return null;
}

/** A pasted bare URL: no blanks (*deep-fix r10, r11*); its scheme is the shared check's (decision 44, L3). */
const PASTED_URL = /^(?:https?:\/\/|mailto:)\S+$/iu;

/** The URL pieces a paste or drop puts in are, if they are one: characters that would end a destination percent-encoded (H4's). */
function pastedUrl(insert: readonly Piece[]): Piece[] | null {
  const [only, ...more] = insert;
  if (only === undefined || more.length > 0 || only.kind !== 'text' || !PASTED_URL.test(only.text) || !isAllowedLink(only.text)) return null;
  let encoded = '';
  for (const char of only.text) {
    const code = char.charCodeAt(0);
    encoded += code < 0x20 || code === 0x7f || '()<>\\'.includes(char) ? `%${code.toString(16).toUpperCase().padStart(2, '0')}` : char;
  }
  return [{ kind: 'text', text: encoded }];
}

/** Part 10b: an empty paragraph's content as stored (S1). */
const EMPTY_PARAGRAPH = /^(?:&nbsp;)+$/u;

/** Part 10b: the empty paragraph whose content holds `from`–`to`, ends included, or null. */
export function emptyParagraphAt(parsed: ParsedChapter, from: number, to = from): SyntaxNode | null {
  for (const side of [-1, 1] as const) {
    for (let node: SyntaxNode | null = parsed.tree.resolveInner(from, side); node !== null; node = node.parent) {
      if (node.name !== 'Paragraph') continue;
      if (node.from <= from && to <= node.to && EMPTY_PARAGRAPH.test(parsed.text.slice(node.from, node.to))) return node;
      break;
    }
  }
  return null;
}

/** Table P: where typed or pasted units go, and as what. */
function place<E extends Edit>(parsed: ParsedChapter, units: readonly Unit[], edit: LocalEdit<E>, typed: boolean): LocalEdit<E> {
  const { text } = parsed;
  if (edit.insert.length === 0) return edit;
  // P13 (part 10b, T1): what is put in on an empty paragraph replaces its no-break spaces.
  const empty = emptyParagraphAt(parsed, edit.from, edit.to);
  if (empty !== null && (edit.from !== empty.from || edit.to !== empty.to)) return place(parsed, units, { ...edit, from: empty.from, to: empty.to }, typed);
  // P9 (*deep-fix r4*): at the line end after a centered block's `<-` (where End puts the cursor), before the `<-`.
  const mark = parsed.tree.resolveInner(edit.from, -1);
  if (edit.from === edit.to && mark.name === 'CenterMark' && mark.to === edit.from && text.charAt(mark.from) === '<') {
    return place(parsed, units, { ...edit, from: mark.from, to: mark.from }, typed);
  }
  // P11 (*deep-fix r13*): block breaks put in inside a span close it and open it again around each, as Enter's split does.
  const unsplit = (p: Piece): boolean => isSeparator(p) && p.split !== true;
  // At a span's edge, a break goes outside its marks (*deep-fix r14*).
  const edge = edit.from === edit.to && edit.insert.some(unsplit) ? outsideSpans(parsed, edit.from) : edit.from;
  if (edge !== edit.from) return place(parsed, units, { ...edit, from: edge, to: edge }, typed);
  const spans = edit.insert.some(unsplit) ? openSpans(parsed, edit.from, edit.to) : null;
  if (spans !== null && spans.close.length > 0) {
    // Blanks beside a break stay text outside the marks (*deep-fix r14, r15*): the old ones beside the edit come in with
    // it, and a line end before pieces that start with a break is the break's.
    const near = splitBlanks(parsed, edit.from, edit.to, edit.insert[0] !== undefined && unsplit(edit.insert[0]));
    const pieces: Piece[] = [{ kind: 'text', text: near.before }, ...edit.insert, { kind: 'text', text: near.after }];
    return place(parsed, units, { ...edit, from: near.from, to: near.to, insert: splitSpans(pieces, spans) }, typed);
  }
  const centered = centeredContent(parsed, edit.from, edit.to);
  if (centered !== null && edit.insert.some(isSeparator)) {
    const split = splitCentered(edit.insert, edit.to === centered.to);
    // P10: pieces ending in a heading, list, quote or scene break at the block's end take its closing mark.
    return place(parsed, units, { ...edit, to: split.swallow ? centered.to + 2 : edit.to, insert: split.insert }, typed);
  }
  const asMarkup = (kind: PieceKind, at: LocalEdit<E> = edit): LocalEdit<E> => ({ ...at, insert: at.insert.map((p) => (p.kind === 'text' ? { kind, text: p.text, prefixed: true } : p)) });
  // P2 (*deep-fix r10*): a link the writer edits in place: what they type, or a URL with an allowed scheme they paste or
  // drop, goes into a destination, title or label as the value it edits (decision 44 still holds: no other scheme, and
  // nothing that would end the value, comes in by paste).
  const zone = valueZone(units, edit.from, edit.to);
  const url = zone === 'value' && !typed ? pastedUrl(edit.insert) : null;
  if (zone !== null && (typed || url !== null)) {
    // An emptied destination's `<>`: between the brackets.
    const inside = edit.from === edit.to && text.startsWith('<>', edit.from) ? { ...edit, from: edit.from + 1, to: edit.from + 1 } : edit;
    return asMarkup(zone === 'label' ? 'block' : 'inline', { ...inside, insert: url ?? inside.insert });
  }
  if (edit.from !== edit.to) return { ...edit, insert: prefixLines(edit.insert, continuation(parsed, edit.from)) };
  const line = lineOf(text, units, edit.from);
  // P4 (part 10a, *deep-fix r5*): anywhere on a blank line (before or between a blank quote line's marks, where Home and
  // a click put the cursor) is its end: the paragraph typed there goes in after the line's marks, never in front of them.
  const blank = line.units.every((u) => u.kind === 'block');
  if (blank && edit.from < line.end && /^[ \t>]*$/u.test(text.slice(line.start, line.end))) {
    return place(parsed, units, { ...edit, from: line.end, to: line.end }, typed);
  }
  if (edit.from === line.end && blank) {
    const marks = line.units.filter((u) => !isBlank(u.show));
    if (marks.at(-1)?.mark === 'opener') {
      // P5: an empty heading, item or note takes the text as its content. A blank the writer starts with is the one
      // between mark and content.
      const content = prefixLines(edit.insert, continuation(parsed, line.end)).map((p, i) => (i === 0 && p.kind === 'text' && isBlank(p.text.charAt(0)) ? { ...p, text: p.text.slice(1) } : p));
      return { ...edit, insert: [{ kind: 'block', text: ' ' }, ...content.filter((p) => p.text !== '')] };
    }
    // P4: a blank line, or the empty line after the last: a new paragraph there, in the line's containers.
    const inner = continuation(parsed, line.end);
    return {
      ...edit,
      insert: [{ kind: 'block', text: `\n${inner}`, prefixed: true }, ...prefixLines(edit.insert, inner)],
      after: [{ kind: 'block', text: `\n${inner.trimEnd()}`, prefixed: true }],
    };
  }
  const before = units.find((u) => u.to === edit.from);
  // P3: in or right after a heading's `#` run or a list mark, or right after a quote mark, typing.
  if (typed && (before?.mark ?? null) !== null) return asMarkup('block');
  return { ...edit, insert: prefixLines(edit.insert, continuation(parsed, edit.from)) };
}

/** Where an edit's units lie among the entries: from `start` to `end`, then its trailing markup. */
interface Span<E extends Edit> {
  readonly origin: E;
  readonly start: number;
  readonly end: number;
}

/** A window's units after its edits, and where each edit's units lie. */
interface Edited<E extends Edit> {
  readonly entries: readonly Entry[];
  readonly spans: readonly Span<E>[];
  /** For each entry, whether its line holds an edit (K): a line runs between line ends the writer sees. */
  readonly touched: readonly boolean[];
}

/** An inserted piece's entries, one per UTF-16 unit (a pasted surrogate pair is never split by an edit). */
function inserted(piece: Piece): Entry[] {
  const out: Entry[] = [];
  for (let i = 0; i < piece.text.length; i += 1) {
    const char = piece.text.charAt(i);
    out.push({ show: char, raw: char, kind: piece.kind, origin: null, at: -1, inserted: true, keep: piece.keep === true });
  }
  return out;
}

function edited<E extends Edit>(text: string, units: readonly Unit[], edits: readonly LocalEdit<E>[]): Edited<E> {
  const entries: Entry[] = [];
  const spans: Span<E>[] = [];
  /** Entry indices at which an edit was made: before and after what it put in. */
  const marks = new Set<number>();
  let skipTo = -1;
  const make = (edit: LocalEdit<E>): void => {
    marks.add(entries.length);
    const start = entries.length;
    for (const piece of edit.insert) entries.push(...inserted(piece));
    spans.push({ origin: edit.origin, start, end: entries.length });
    for (const piece of edit.after) entries.push(...inserted(piece));
    marks.add(entries.length);
    skipTo = Math.max(skipTo, edit.to);
  };
  const queue = edits.values();
  let next = queue.next();
  for (const unit of units) {
    for (; !next.done && next.value.from <= unit.from; next = queue.next()) make(next.value);
    if (unit.from < skipTo) continue;
    const raw = text.slice(unit.from, unit.to);
    entries.push({ show: unit.show, raw, kind: unit.kind, origin: unit.origin, at: unit.from, inserted: false, keep: false });
  }
  for (; !next.done; next = queue.next()) make(next.value);
  // A line is touched when an edit was made on it: a mark touches the line of the entry after it (the last line at the
  // end); a line runs up to and with its line end.
  const lines: number[] = [];
  const touchedLines = new Set<number>();
  let line = 0;
  for (const [i, entry] of entries.entries()) {
    if (marks.has(i)) touchedLines.add(line);
    lines.push(line);
    if (isLineEnd(entry.show)) line += 1;
  }
  if (marks.has(entries.length)) touchedLines.add(line);
  return { entries, spans, touched: lines.map((l) => touchedLines.has(l)) };
}

/** Whether an entry is an old backslash hard break's `\`; one an edit puts in is placed where its edit means it. */
const isBreakMark = (entry: Entry): boolean => !entry.inserted && entry.kind === 'inline' && entry.show === '\\';

/** Whether an entry is inline markup's line end: a hard or soft break's. */
const isBreakEnd = (entry: Entry | undefined): boolean => entry?.kind === 'inline' && isLineEnd(entry.show);

/**
 * Whether text the writer sees (text, a note's label or number, a link's value) lies on the line beside a break, from
 * entry `i` on by `step` up to the nearest line end of any kind (entries in `gone` are nothing); and whether an edit was
 * made on that line. Each look reads one line, so a paragraph of many breaks costs its length once.
 */
function contentBeside(ed: Edited<Edit>, i: number, step: 1 | -1, gone: ReadonlySet<number>): { found: boolean; touched: boolean } {
  let touched = false;
  for (let k = i; ; k += step) {
    const entry = ed.entries[k];
    if (entry === undefined) return { found: false, touched };
    if (gone.has(k)) continue;
    // A line end belongs to the line before it: going forward it ends the line looked at, going back it doesn't.
    if (isLineEnd(entry.show)) return { found: false, touched: touched || (step === 1 && ed.touched[k] === true) };
    touched ||= ed.touched[k] === true;
    // A break an edit puts in (Shift+Enter) is a line the writer asked for: the line it makes stays (*deep-fix r19*).
    if (entry.kind === 'text' || entry.kind === 'label' || entry.kind === 'value' || (entry.inserted && entry.keep && entry.show === '\\')) return { found: true, touched };
  }
}

/**
 * Table B (*deep-fix r7, r8, r9*): an old backslash break's `\` is never written as text (the user's choice after
 * deep-fix r9: what is typed or composed right after it goes before it, P6). When the edits take its line end from
 * after it (B4), it goes. A break makes a line; when the edits leave the line after it, or the line it ends, with no
 * text (B2, B3), that line can't be stored, so the break goes with its line end and the next line's container marks,
 * and the other line end around the emptied line stays. Breaks are looked at in order, so of two around one emptied
 * line only the first goes. A side no edit touched is left as it is (G6).
 */
function strayBreaksGone<E extends Edit>(ed: Edited<E>, stray: ReadonlySet<number>, units: readonly Unit[]): Edited<E> {
  // B8: a `\\` a written window read as text goes; its line end stays, as the other kind it became.
  const gone = new Set<number>(stray);
  /**
   * B7 (*deep-fix r22*): whether the line beside an old break, from the unit at `from` by `step` up to the nearest line
   * end, held text before the edits: only a line the edits emptied goes; one empty already (a blank line two breaks
   * make, a break at a leaf's start) is as it was.
   */
  const hadText = (from: number, step: 1 | -1): boolean => {
    const way = step === 1 ? units.filter((u) => u.from >= from) : units.filter((u) => u.to <= from).reverse();
    for (const u of way) {
      if (isLineEnd(u.show)) return false;
      // A note's label is its mark, not the line's text.
      if (u.kind === 'text' || u.kind === 'value') return true;
    }
    return false;
  };
  for (const [i, entry] of ed.entries.entries()) {
    if (!isBreakMark(entry) || gone.has(i)) continue;
    // B4: its line end no longer after it, it goes: a break's `\` is never text (*deep-fix r9*, the user's choice).
    if (!isBreakEnd(ed.entries[i + 1])) {
      gone.add(i);
      continue;
    }
    const before = contentBeside(ed, i - 1, -1, gone);
    const after = contentBeside(ed, i + 2, 1, gone);
    // The break's own entries are on the line before it.
    const emptiedBefore = !before.found && (before.touched || ed.touched[i] === true) && hadText(entry.at, -1);
    const emptiedAfter = !after.found && after.touched && hadText(entry.at + 2, 1);
    if (emptiedBefore || emptiedAfter) {
      gone.add(i).add(i + 1);
      // The container marks its next line starts with go with it: that line joins this one.
      for (let k = i + 2; ; k += 1) {
        const mark = ed.entries[k];
        if (mark?.kind !== 'block' || isLineEnd(mark.show)) break;
        gone.add(k);
      }
    }
  }
  if (gone.size === 0) return ed;
  return { ...ed, entries: ed.entries.map((entry, i) => (gone.has(i) ? { ...entry, show: '', raw: '' } : entry)) };
}

/**
 * R10 (*deep-fix r20*): a span the edits leave with no text between its marks (an emphasis, strong or small caps whose
 * word was deleted, a link whose text was) goes, marks and destination with it: nothing of it would show, as part 04's
 * `replaceText` decides (its K1). A span with text left, or put in, stays; one empty before the edits stays as it was.
 */
function emptySpansGone<E extends Edit>(ed: Edited<E>, extents: readonly Extent[], units: readonly Unit[], edits: readonly { from: number; to: number }[]): Edited<E> {
  const gone = new Set<number>();
  // Only a span an edit reaches can be emptied: the rest are not looked at, so the cost follows the edit.
  for (const extent of extents.filter((x) => edits.some((e) => x.from <= e.to && x.to >= e.from))) {
    // Only a span the edits emptied: one already empty (`[](u)`) is as it was (G6).
    if (!extent.span || !units.some((u) => u.kind === 'text' && u.from >= extent.from && u.to <= extent.to)) continue;
    const held = ed.entries.flatMap((entry, i) => (entry.at >= extent.from && entry.at < extent.to && entry.show !== '' ? [i] : []));
    const first = held[0];
    const last = held.at(-1);
    if (first === undefined || last === undefined) continue;
    const inside = ed.entries.slice(first, last + 1);
    if (inside.some((entry) => entry.show !== '' && (entry.kind === 'text' || entry.kind === 'label'))) continue;
    // A construct in it that isn't a span (a note reference, an image) is something that shows (*deep-fix r21*).
    const shows = extents.some((x) => !x.span && !x.lineBreak && x.from >= extent.from && x.to <= extent.to && inside.some((entry) => entry.show !== '' && entry.at >= x.from && entry.at < x.to));
    if (shows) continue;
    for (let i = first; i <= last; i += 1) gone.add(i);
  }
  if (gone.size === 0) return ed;
  return { ...ed, entries: ed.entries.map((entry, i) => (gone.has(i) ? { ...entry, show: '', raw: '' } : entry)) };
}

/** Table K: one text character, escaped for wherever it lands; `edge` when a line edge or a blank is beside it. */
function writeText(char: string, edge: boolean): string {
  if (PUNCTUATION.test(char)) return `\\${char}`;
  if (char === '\n') return '&#10;';
  if (char === '\r') return '&#13;';
  if (edge && char === ' ') return '&#32;';
  if (edge && char === '\t') return '&#9;';
  return char;
}

/** An entry as a stage writes it: as text (table K) or as the markup it is. */
interface Cell {
  readonly entry: Entry;
  readonly text: boolean;
  readonly touched: boolean;
  /** Old inline markup of a construct the edits don't meet: it must stay markup (V1). */
  readonly free: boolean;
  /** Old text of a delimiter run whose construct a stage writes as text: escaped too, so it can't pair (V2–V4). */
  readonly loose: boolean;
  /** K3a: a text blank that counts a span's delimiter beside it as an edge (*deep-fix r18, r21*). */
  readonly spanEdge: boolean;
}

/** Whether a cell is a line edge or blank markup, as K3 sees a neighbour; none (the window's edge) is one. */
function edgeLike(cell: Cell | undefined, spanEdges: boolean): boolean {
  if (cell === undefined) return true;
  if (spanEdges && !cell.text && cell.entry.kind === 'inline' && SPAN_DELIMITER.test(cell.entry.show)) return true;
  return isLineEnd(cell.entry.show) || (!cell.text && (cell.entry.kind === 'block' || isBlank(cell.entry.show)));
}

/** A span's delimiter: a mark beside which a blank would stop it opening or closing (K3a, *deep-fix r18*). */
const SPAN_DELIMITER = /^[*_^]$/u;

/** Whether a cell is a blank written as text. */
function textBlank(cell: Cell | undefined): boolean {
  return cell !== undefined && cell.text && isBlank(cell.entry.show);
}

/**
 * K3: whether the text blank in cell `i` is in a run of text blanks that reaches a line edge (a line end, block markup,
 * markup blanks, the window's edge) on either side, where a line would strip it or a break swallow it. A run inside a
 * line stays blanks: a reference there would read as punctuation beside a delimiter (L6).
 */
function atEdge(cells: readonly Cell[], i: number, spanEdges: boolean): boolean {
  // A break table B took away is nothing there (*deep-fix r7*).
  const passed = (cell: Cell | undefined): boolean => textBlank(cell) || cell?.entry.show === '';
  let before = i - 1;
  while (passed(cells[before])) before -= 1;
  let after = i + 1;
  while (passed(cells[after])) after += 1;
  return edgeLike(cells[before], spanEdges) || edgeLike(cells[after], spanEdges);
}

/**
 * The window written as a stage says (table K). `tidy` writes references and escapes on a touched line by table K too,
 * so a reference the canonical form kept beside a delimiter (part 04, E23) goes once the text around it allows (stage 0
 * of table V). `everywhere` writes text on every line by table K (V4): no text left as it was can then pair with
 * anything, wherever the edit moved a bracket or delimiter.
 */
function candidate(cells: readonly Cell[], tidy: boolean, everywhere: boolean): string {
  let out = '';
  for (const [i, cell] of cells.entries()) {
    const { entry } = cell;
    if (!cell.text) {
      // K6: a hard break's backslash with no line end after it is a backslash.
      const next = cells[i + 1];
      const breaks = next !== undefined && isLineEnd(next.entry.show) && !next.text;
      out += entry.raw === '\\' && entry.kind !== 'value' && entry.kind !== 'label' && !breaks ? '\\\\' : entry.raw;
      continue;
    }
    // Old text as it was: off the touched lines; a reference unless tidying; a delimiter run's part unless everywhere.
    const asItWas = (!cell.touched && !everywhere) || (entry.origin === 'reference' && !tidy) || (entry.origin === 'delimiter' && !everywhere && !cell.loose);
    if (entry.kind === 'text' && !entry.inserted && asItWas) {
      out += entry.raw;
      continue;
    }
    const edge = isBlank(entry.show) && atEdge(cells, i, cell.spanEdge);
    for (const char of entry.show) out += writeText(char, edge);
  }
  return out;
}

/** Each display character's unit kind: `t` (text), `i` (inline markup or a value) or `b` (block markup or a label). */
function kindsOf(units: readonly Unit[]): string {
  return units.map((u) => (u.kind === 'text' ? 't' : u.kind === 'inline' || u.kind === 'value' ? 'i' : 'b').repeat(u.show.length)).join('');
}

/**
 * V1: the written window's units show what the cells do: cells written as text as text, conversions as markup, the
 * markup of constructs the edits don't meet as markup (an edit breaks only what it meets, principle 3), and inline
 * markup of any construct as inline markup or text, never block markup (a closing `*` left at a line's start is no list
 * item, *deep-fix r1*); with `strictMarkup` (stage 0), all markup as markup.
 */
function holds(cells: readonly Cell[], units: readonly Unit[], strictMarkup: boolean): boolean {
  const expected = cells.map((c) => c.entry.show).join('');
  if (units.map((u) => u.show).join('') !== expected) return false;
  /** `t` text; `c` markup; `n` inline markup or text; `m` anything. */
  const want = cells
    .map((c) => {
      // A line end may become the one between blocks (a soft break whose line now starts a quote, *deep-fix r2*).
      const inlineMarkup = (c.entry.kind === 'inline' || c.entry.kind === 'value') && !isLineEnd(c.entry.show);
      const kind = c.text ? 't' : c.entry.kind === 'convert' || c.free || strictMarkup ? 'c' : inlineMarkup ? 'n' : 'm';
      return kind.repeat(c.entry.show.length);
    })
    .join('');
  const got = kindsOf(units);
  for (let i = 0; i < want.length; i += 1) {
    const kind = want.charAt(i);
    const actual = got.charAt(i);
    if ((kind === 't' && actual !== 't') || (kind === 'c' && actual === 't') || (kind === 'n' && actual === 'b')) return false;
  }
  return true;
}

/**
 * Where a point goes in the canonical window: right after display character `index - 1` of the written window. Text is
 * the same in both, in order (principle 2), so a point beside text keeps its place by the text: after the text before
 * it, or before the text after it. A point with markup on both sides (an empty item's mark) keeps its place counted
 * from the nearer end that the canonical form left as it was.
 */
function pointIn(written: readonly Unit[], result: readonly Unit[], index: number, length: number): number {
  const writtenKinds = kindsOf(written);
  let textBefore = 0;
  for (let i = 0; i < index; i += 1) if (writtenKinds.charAt(i) === 't') textBefore += 1;
  // In one pass: the point after the result's text character `textBefore` (its start for none), and the point before
  // the next one (its end for none).
  let afterText = 0;
  let beforeNext = length;
  let seen = 0;
  for (const unit of result) {
    if (unit.kind !== 'text') continue;
    if (seen === textBefore) beforeNext = Math.min(beforeNext, unit.from);
    seen += unit.show.length;
    if (seen <= textBefore) afterText = unit.to;
  }
  if (writtenKinds.charAt(index - 1) === 't') return afterText;
  if (writtenKinds.charAt(index) === 't') return beforeNext;
  const expected = written.map((u) => u.show).join('');
  const shown = result.map((u) => u.show).join('');
  let prefix = 0;
  while (prefix < expected.length && prefix < shown.length && expected.charAt(prefix) === shown.charAt(prefix)) prefix += 1;
  let suffix = 0;
  while (suffix < expected.length - prefix && suffix < shown.length - prefix && expected.charAt(expected.length - 1 - suffix) === shown.charAt(shown.length - 1 - suffix)) suffix += 1;
  /** Right after the unit holding display character `at - 1`; the window's start for 0. */
  const after = (at: number): number => {
    let point = 0;
    let count = 0;
    for (const unit of result) {
      if (count < at) point = unit.to;
      count += unit.show.length;
    }
    return point;
  };
  if (index <= prefix) return after(index);
  if (expected.length - index <= suffix) return after(shown.length - (expected.length - index));
  // Inside the span that changed: by its characters, when the result's are the written ones with some left out (a
  // destination's `<>` dropped, *deep-fix r10*); else at its start.
  return after(prefix + alongside(expected.slice(prefix, expected.length - suffix), shown.slice(prefix, shown.length - suffix), index - prefix));
}

/**
 * Where display character `k` of `from` falls in `to`, when `to` is `from` with characters left out (a destination's `<>`
 * dropped); else 0, the span's start (a span that gained characters, as an emptied destination's `<>`).
 */
function alongside(from: string, to: string, k: number): number {
  if (!isSubsequence(to, from)) return 0;
  let j = 0;
  for (let i = 0; i < k; i += 1) j += from.charAt(i) === to.charAt(j) ? 1 : 0;
  return j;
}

/** Whether `short`'s characters are in `long`, in order. */
function isSubsequence(short: string, long: string): boolean {
  let j = 0;
  for (let i = 0; i < long.length && j < short.length; i += 1) if (long.charAt(i) === short.charAt(j)) j += 1;
  return j === short.length;
}

/** One window rewritten: its new text and where each edit ended up in it. */
interface WindowResult<E extends Edit> {
  readonly text: string;
  readonly points: readonly Point<E>[];
  readonly stage: Stage;
}

/** One step of table V: which entries a stage writes as text, whether it tidies (stage 0), and whether it is checked. */
interface Step {
  readonly stage: Stage;
  readonly tidy: boolean;
  /** Whether a stage writes an entry as text, given whether its line is touched and whether the edits meet its construct. */
  readonly asText: (entry: Entry, touched: boolean, met: boolean) => boolean;
  /**
   * K3a (*deep-fix r18*): text blanks beside a span's delimiter are written as references too, so a blank typed at a
   * span's inner edge (`*really *`) keeps it a span; the canonical form writes back as a blank any it doesn't need.
   */
  readonly spanEdges?: boolean;
  /**
   * Fix-next 32: the candidate is taken only when all markup stays markup, as stage 0's is: a reference written beside a
   * delimiter changes its flanking, and can unmake the very span it was written to keep (`* *\[` with a run of `**`
   * after: the rule of three). A step that fails so is passed over, never the end of a strict rewrite.
   */
  readonly strictMarkup?: boolean;
}

/** A narrowed window (G15) read otherwise than the chapter would read it: the edits go to G1's window. */
const WIDER = Symbol('wider');

/**
 * Top-level blocks a conversion can be made in that a blank line closes, so the neighbours read the same beside them
 * whatever they hold (G11, G12).
 */
const CLOSED: ReadonlySet<string> = new Set([
  'Paragraph',
  'ATXHeading1',
  'ATXHeading2',
  'ATXHeading3',
  'ATXHeading4',
  'ATXHeading5',
  'ATXHeading6',
  'SetextHeading1',
  'SetextHeading2',
  'CenteredBlock',
  'Blockquote',
]);

/**
 * G15: whether a narrowed window's parse holds closed blocks only (no list, note or link definition at the top level),
 * so it reads beside the neighbours as it reads alone, and at least one, so the blank lines on each side still separate
 * two blocks (*build*: a conversion that emptied its block left a stray line end). Its first line is never indented: the
 * window starts at the block's first character, and a conversion writes a mark there, a blank only as a reference (K3).
 */
export function readsAlone(parsed: ParsedChapter): boolean {
  const first = parsed.tree.topNode.firstChild;
  for (let block = first; block !== null; block = block.nextSibling) if (!CLOSED.has(block.name)) return false;
  return first !== null;
}

/** `text` read: the reading given when it is of this text (G18), else parsed. */
function readingOf(text: string, given: Reading | undefined): Reading {
  if (given?.parsed.text === text) return given;
  const parsed = parseChapter(text);
  const model = chapterModel(parsed);
  return { parsed, model, units: unitsOf(parsed, model) };
}

/**
 * Table V's stages over one window's edits; null when `strict` and the edits don't hold as made (S10). `narrowed` (only
 * with `strict`): the window is a closed block cut out of its chapter (G11, G12), and a stage's candidate that wouldn't
 * read there as here gives WIDER (G15). The window's own text is that block (`blockAlone` read its kind in the chapter's
 * parse), and the canonical result reads as the candidate it writes (part 04: the canonical form keeps the model), so
 * neither needs the check.
 */
function rewriteWindow<E extends Edit>(text: string, edits: readonly LocalEdit<E>[], strict: boolean, narrowed: false, given: Reading | undefined, stray?: ReadonlySet<number>): WindowResult<E> | null;
function rewriteWindow<E extends Edit>(text: string, edits: readonly LocalEdit<E>[], strict: boolean, narrowed: boolean, given: Reading | undefined, stray?: ReadonlySet<number>): WindowResult<E> | null | typeof WIDER;
function rewriteWindow<E extends Edit>(
  text: string,
  edits: readonly LocalEdit<E>[],
  strict: boolean,
  narrowed: boolean,
  given: Reading | undefined,
  stray: ReadonlySet<number> = new Set(),
): WindowResult<E> | null | typeof WIDER {
  const { parsed, model, units } = readingOf(text, given);
  const placed = edits.map((edit) => {
    // P7: a point inside an escape or entity goes before it; a range takes all of it.
    const from = units.find((u) => u.from < edit.from && u.to > edit.from)?.from ?? edit.from;
    const to = edit.to === edit.from ? from : (units.find((u) => u.from < edit.to && u.to > edit.to)?.to ?? edit.to);
    const widened = { ...edit, from, to };
    return edit.origin.place === true ? place(parsed, units, widened, edit.origin.typed === true) : widened;
  });
  const made = strayBreaksGone(edited(text, units, placed), stray, units);
  const ed = emptySpansGone(made, extentsOf(model.blocks), units, placed);
  // Fix-next 9: a conversion's own units are markup, no text, so one whose marks were all a span held would empty it
  // and R10 would take the span, and the writer's character with it. A conversion never takes text away: it isn't made.
  if (strict && ed !== made) return null;
  // A span's delimiter run reaches into the unmatched part written beside it (L6): an edit there meets the span. Each
  // such part is one character.
  const delimiters = new Set(units.filter((u) => u.origin === 'delimiter').map((u) => u.from));
  const extents = extentsOf(model.blocks).map((x) => {
    let { from, to } = x;
    while (delimiters.has(from - 1)) from -= 1;
    while (delimiters.has(to)) to += 1;
    return { ...x, from, to };
  });
  /**
   * A unit's own construct: the innermost holding it (a link's markup is the link's, its emphasis's marks the
   * emphasis's). Extents come parents first, so the innermost is the last that holds it.
   */
  const own = (at: number): Extent | null => extents.filter((x) => at >= x.from && at < x.to).at(-1) ?? null;
  /** Whether the edits meet the construct of the old unit at `at` (an inserted unit, at -1, has none). */
  const meets = (at: number): boolean => {
    const element = own(at);
    return element !== null && placed.some((e) => element.from <= e.to && element.to >= e.from);
  };
  /** V1: old inline markup an edit may not break: a construct's (not a line break's) the edits don't meet. */
  const free = (entry: Entry, met: boolean): boolean => inline(entry) && !entry.inserted && own(entry.at)?.lineBreak === false && !met;
  /** Inline markup (V2, V3): an inline element's own, or its value; never a line end, never what a command put in. */
  const inline = (entry: Entry): boolean => (entry.kind === 'inline' || entry.kind === 'value') && !isLineEnd(entry.show) && !entry.keep;
  /** Block markup (V4): a block's own, or its label; never a line end, never what a command put in. */
  const block = (entry: Entry): boolean => (entry.kind === 'block' || entry.kind === 'label') && !isLineEnd(entry.show) && !entry.keep;
  const asIs = (entry: Entry): boolean => entry.kind === 'text';
  // Every stage looks only at what the edits touched, so the result doesn't depend on how wide the window is (G5).
  // K3a: an edit at a span's inner edge (right after its opening mark, right before its closing one) first tries its
  // blanks there as references, which keep the marks a span's (*deep-fix r18*).
  const innerEdge = placed.some((e) => e.from === e.to && e.insert.some((p) => p.kind === 'text' && /^\s|\s$/u.test(p.text)) && outsideSpans(parsed, e.from) !== e.from);
  // And an edit inside a span (a deletion there can leave an old blank at its edge, *deep-fix r21*).
  const inSpan = placed.some((e) => extents.some((x) => x.span && x.from < e.from && e.to < x.to));
  // Fix-next 32, table KS: the first candidate that keeps every mark is taken, of the three that can (K3a's, stage 0's,
  // stage 1's as it is), in that order; only when none does, the steps that may lose one, in the order they had (K3a's,
  // stage 1's, then stages 2 and 3).
  const spanEdgeSteps: readonly Step[] = innerEdge || inSpan ? [{ stage: 1, tidy: false, asText: asIs, spanEdges: true, strictMarkup: true }] : [];
  const steps: readonly Step[] = [
    ...spanEdgeSteps,
    { stage: 1, tidy: true, asText: asIs },
    ...(spanEdgeSteps.length > 0 ? [{ stage: 1 as const, tidy: false, asText: asIs, strictMarkup: true }] : []),
    ...spanEdgeSteps.map((step) => ({ ...step, strictMarkup: false })),
    { stage: 1, tidy: false, asText: asIs },
    { stage: 2, tidy: false, asText: (entry, _touched, met) => asIs(entry) || (inline(entry) && (met || entry.inserted)) },
    { stage: 3, tidy: false, asText: (entry, touched, met) => asIs(entry) || (inline(entry) && (met || entry.inserted)) || (block(entry) && touched) },
  ];
  // V4, the last stage: also block markup on touched lines, and every text unit by table K. It always holds: every text
  // unit is then escaped or a reference (K2–K5), and markup may become text (principle 3).
  const last: Step = { stage: 4, tidy: false, asText: (entry, touched) => asIs(entry) || ((inline(entry) || block(entry)) && touched) };
  // Every parse of a written text is made once: each stage's candidate (stages that write the same candidate share it),
  // and the canonical result, which `finish` parses only when no stage wrote it (G18).
  const read = new Map<string, { readonly model: ChapterModel; readonly units: readonly Unit[]; readonly alone: boolean }>();
  const readText = (source: string): { readonly model: ChapterModel; readonly units: readonly Unit[]; readonly alone: boolean } => {
    const known = read.get(source);
    if (known !== undefined) return known;
    const writtenParsed = parseChapter(source);
    const writtenModel = chapterModel(writtenParsed);
    const value = { model: writtenModel, units: unitsOf(writtenParsed, writtenModel), alone: !narrowed || readsAlone(writtenParsed) };
    read.set(source, value);
    return value;
  };
  // What each entry is, looked at once for every stage.
  const facts = ed.entries.map((entry, i) => {
    const met = meets(entry.at);
    return { entry, touched: ed.touched[i] === true, met, free: free(entry, met) };
  });
  const attempt = (step: Step): { cells: Cell[]; source: string; model: ChapterModel; units: readonly Unit[]; alone: boolean } => {
    const cells = facts.map(({ entry, touched, met, free: kept }) => ({
      entry,
      touched,
      text: step.asText(entry, touched, met),
      free: kept,
      loose: step.stage > 1 && entry.origin === 'delimiter' && met,
      // K3a: a blank the edit put in at a span's inner edge, or an old one inside a span that a deletion left there.
      spanEdge: step.spanEdges === true && (entry.inserted ? innerEdge : own(entry.at)?.span === true),
    }));
    const source = candidate(cells, step.tidy, step === last);
    return { cells, source, ...readText(source) };
  };
  const finish = (step: Step, written: { source: string; model: ChapterModel; units: readonly Unit[] }): WindowResult<E> => {
    const out = serializeChapter(written.model);
    // Most often the written window is in canonical form already, and reads as it was read.
    const resultUnits = readText(out).units;
    const point = (n: number): number => {
      const shown = ed.entries.slice(0, n).reduce((sum, entry) => sum + entry.show.length, 0);
      return pointIn(written.units, resultUnits, shown, out.length);
    };
    return { text: out, points: ed.spans.map((span) => ({ edit: span.origin, start: point(span.start), end: point(span.end) })), stage: step.stage };
  };
  /** B8: the written window, unless an old break's `\\` reads as text there; then again without that `\\`. */
  const settle = (step: Step, written: ReturnType<typeof attempt>): WindowResult<E> | null | typeof WIDER => {
    const read = breaksAsText(written.cells, written.units);
    return read.length === 0 ? finish(step, written) : rewriteWindow(text, edits, strict, narrowed, given, new Set([...stray, ...read]));
  };
  for (const step of steps) {
    const written = attempt(step);
    if (!written.alone) return WIDER;
    const keepsMarkup = step.tidy || step.strictMarkup === true;
    if (holds(written.cells, written.units, keepsMarkup)) return settle(step, written);
    if (strict && !keepsMarkup) return null;
  }
  // Only a rewrite that isn't strict gets here, never narrowed.
  return settle(last, attempt(last));
}

/**
 * B8 (*deep-fix r10*): the entries of old breaks' `\\` that the written window reads as text: their line end became
 * another kind (a conversion made the line a heading, a heading pasted at the next line's start).
 */
function breaksAsText(cells: readonly Cell[], units: readonly Unit[]): number[] {
  const got = kindsOf(units);
  const read: number[] = [];
  let at = 0;
  for (const [i, { entry }] of cells.entries()) {
    if (isBreakMark(entry) && entry.show !== '' && got.charAt(at) === 't') read.push(i);
    at += entry.show.length;
  }
  return read;
}

/** Leaf blocks: no link reference definition is inside one. */
const LEAF_NODES = /^(?:Paragraph|ATXHeading\d|SetextHeading\d|CenteredBlock|SceneBreak|LinkReference)$/u;

/**
 * G4: whether the chapter holds a link reference definition anywhere. Links read alone don't resolve through it, so
 * any window would read them differently; only a chapter not in canonical form has one. Visits blocks, not their text.
 */
function holdsDefinition(parsed: ParsedChapter): boolean {
  let found = false;
  parsed.tree.iterate({
    enter: (node) => {
      if (node.name === 'LinkReference') found = true;
      return !found && !LEAF_NODES.test(node.name);
    },
  });
  return found;
}

/** How `rewrite` runs. */
export interface RewriteOptions {
  /** One window over the whole chapter: G5's oracle for the windowed rewrite. */
  readonly whole?: boolean;
}

/** How `convert` runs; `whole` is G5's oracle here too (G19). */
export interface ConvertOptions extends RewriteOptions {
  /** An inline conversion (S2–S5): tried in its top-level block alone first (G11, G12); a block shortcut keeps G1 (G16). */
  readonly inline?: boolean;
  /** The source the conversion read already (its leaf's lines), reused when it is a window's text (G18). */
  readonly reading?: Reading;
}

/**
 * G11–G14: the top-level block holding `from`–`to` (every edit of a group), alone with its line end, when it is a kind
 * a blank line closes and one blank line separates it from each neighbour (or the chapter's edge); else null.
 */
function blockAlone(parsed: ParsedChapter, from: number, to: number): { from: number; to: number } | null {
  const source = parsed.text;
  // The last block starting at or before `from`; none before the first block (a chapter opening with blank lines).
  const block = parsed.tree.topNode.childBefore(from + 1);
  if (block === null || !CLOSED.has(block.name) || to > block.to) return null;
  const { prevSibling: left, nextSibling: right } = block;
  const before = left === null ? block.from === 0 : source.slice(left.to, block.from) === '\n\n';
  const after = right === null ? ['', '\n'].includes(source.slice(block.to)) : source.slice(block.to, right.from) === '\n\n';
  return before && after ? { from: block.from, to: right === null ? source.length : block.to + 1 } : null;
}

/**
 * G6: the window's new text as one change replacing only what differs, and its points in the new source; returns how
 * much longer the source got.
 */
function splice<E extends Edit>(source: string, window: { readonly from: number; readonly to: number }, text: string, result: WindowResult<E>, changes: SourceChange[], points: Point<E>[], delta: number): number {
  const at = window.from;
  const old = source.slice(at, window.to);
  let head = 0;
  while (head < old.length && head < text.length && old.charCodeAt(head) === text.charCodeAt(head)) head += 1;
  let tail = 0;
  while (tail < old.length - head && tail < text.length - head && old.charCodeAt(old.length - 1 - tail) === text.charCodeAt(text.length - 1 - tail)) tail += 1;
  changes.push({ from: at + head, to: window.to - tail, insert: text.slice(head, text.length - tail) });
  for (const point of result.points) points.push({ edit: point.edit, start: at + delta + point.start, end: at + delta + point.end });
  return text.length - old.length;
}

/** Each edit in the positions of a window starting at `at`, with the edit it came from. */
function localEdits<E extends Edit>(edits: readonly E[], at: number): LocalEdit<E>[] {
  return edits.map((origin) => ({ from: origin.from - at, to: origin.to - at, insert: origin.insert, after: origin.after ?? [], origin }));
}

/** Rewrites each group of edits in its window (tables G, V); null when strict and a window's edits don't hold as made. */
function run<E extends Edit>(parsed: ParsedChapter, edits: readonly E[], strict: false, whole: boolean): Rewritten<E>;
function run<E extends Edit>(parsed: ParsedChapter, edits: readonly E[], strict: true, whole: boolean, options: ConvertOptions): Rewritten<E> | null;
function run<E extends Edit>(parsed: ParsedChapter, edits: readonly E[], strict: boolean, whole: boolean, options: ConvertOptions = {}): Rewritten<E> | null {
  const source = parsed.text;
  const length = source.length;
  const everything = whole || holdsDefinition(parsed);
  // G3: one window per group of edits whose windows overlap. Edits are sorted, so each window ends at or after the last.
  // Each group's edits span `from`–`to`.
  const groups: { window: Window; edits: E[]; from: number; to: number }[] = [];
  for (const edit of edits) {
    const window = everything ? between(null, null, length) : windowAround(parsed, edit.from, edit.to);
    const group = groups.at(-1);
    if (group !== undefined && window.from <= group.window.to) {
      group.window = { ...window, from: group.window.from, left: group.window.left };
      group.edits.push(edit);
      group.to = edit.to;
    } else {
      groups.push({ window, edits: [edit], from: edit.from, to: edit.to });
    }
  }
  const changes: SourceChange[] = [];
  const points: Point<E>[] = [];
  const windows: { from: number; to: number }[] = [];
  let delta = 0;
  let stage: Stage = 1;
  const keep = (window: { readonly from: number; readonly to: number }, result: WindowResult<E>): void => {
    delta += splice(source, window, result.text, result, changes, points, delta);
    if (result.stage > stage) stage = result.stage;
    windows.push({ from: window.from, to: window.to });
  };
  for (const group of groups) {
    let window = group.window;
    // G7: one edit inside a quote is rewritten in a slice of that quote, when its neighbours hold (G8);
    // else in its top-level window (G9).
    const [only, ...more] = group.edits;
    const top = everything || only === undefined || more.length > 0 ? null : parsed.tree.topNode.childBefore(only.from + 1);
    const nested = top !== null && SLICED.test(top.name) && only !== undefined ? nestedWindow(source, top, only.from, only.to) : null;
    if (nested !== null && only !== undefined) {
      const result = rewriteWindow(source.slice(nested.from, nested.to), localEdits([only], nested.from), strict, false, options.reading);
      if (result === null) return null;
      const { text } = result;
      if (text.startsWith(nested.before) && text.endsWith(nested.after) && text.length >= nested.before.length + nested.after.length) {
        keep(nested, result);
        continue;
      }
    }
    // G11, G12: an inline conversion in a closed block one blank line from each neighbour is made in that block alone,
    // unless a parse there reads otherwise than the chapter would (G15); G13, G14, G16, G17 keep G1's window.
    const alone = options.inline === true && !everything ? blockAlone(parsed, group.from, group.to) : null;
    if (alone !== null) {
      const result = rewriteWindow(source.slice(alone.from, alone.to), localEdits(group.edits, alone.from), strict, true, options.reading);
      if (result === null) return null;
      if (result !== WIDER) {
        keep(alone, result);
        continue;
      }
    }
    for (;;) {
      const result = rewriteWindow(source.slice(window.from, window.to), localEdits(group.edits, window.from), strict, false, options.reading);
      if (result === null) return null;
      const { text } = result;
      // G2: a neighbour written differently widens the window on its side.
      const { left, right } = window;
      const wider = {
        left: left === null || text.startsWith(`${source.slice(left.from, left.to)}\n\n`) ? undefined : left.prevSibling,
        right: right === null || text.endsWith(`\n\n${source.slice(right.from, right.to)}\n`) ? undefined : right.nextSibling,
      };
      if (wider.left !== undefined || wider.right !== undefined) {
        window = between(wider.left === undefined ? left : wider.left, wider.right === undefined ? right : wider.right, length);
        continue;
      }
      keep(window, result);
      break;
    }
  }
  return { changes, points, stage, windows };
}

/**
 * Makes `edits` in `parsed` (sorted, apart, in its positions) as tables P, K, V and G say, and returns the changes to
 * its source and where each edit ended up. Pure; costs the windows the edits touch (principle 4).
 */
export function rewrite<E extends Edit>(parsed: ParsedChapter, edits: readonly E[], options: RewriteOptions = {}): Rewritten<E> {
  // Not strict: every window ends in a stage that holds (V4), so there is a result.
  return run(parsed, edits, false, options.whole ?? false);
}

/**
 * A conversion (table S): `edits` made as they are, or null when any doesn't hold as made (S10). An inline one is made
 * in its block alone where that reads as the chapter does (G11–G15), reusing the reading given (G18).
 */
export function convert<E extends Edit>(parsed: ParsedChapter, edits: readonly E[], options: ConvertOptions = {}): Rewritten<E> | null {
  return run(parsed, edits, true, options.whole ?? false, options);
}
